// TimelineContext: Shared context for timeline drag & drop state and handlers
// CREATED: Eliminates prop drilling across DayTimeline -> TimelineColumn -> TimelineTaskBlock
// Provides drag state, event handlers, and shared configuration via React context

import { createContext, useContext } from 'react';
import type { TimelineTask, DragPosition } from '../../../hooks/useDragDropTimeline';

export interface TimelineContextValue {
  draggedTask: TimelineTask | null;
  draggedTaskPosition: DragPosition | null;
  isDragRollback: boolean;
  isTouchDragging: boolean;
  conflictingTaskIds: Set<string>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isMobile: boolean;
  onDragStart: (e: React.DragEvent, task: TimelineTask) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, operatorId: string, timelineElement: HTMLElement | null) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, toOperatorId: string) => void;
  onTouchStart: (e: React.TouchEvent, task: TimelineTask, operatorId: string) => void;
  onTouchMove: (e: React.TouchEvent, operatorId: string, timelineElement: HTMLElement | null) => void;
  onTouchEnd: (e: React.TouchEvent, operatorId: string) => void;
  onTouchCancel: (e: React.TouchEvent) => void;
  onTaskClick?: (task: TimelineTask) => void;
  canReassignTask: (task: TimelineTask) => boolean;
}

const TimelineContext = createContext<TimelineContextValue | null>(null);

export function useTimelineContext(): TimelineContextValue {
  const ctx = useContext(TimelineContext);
  if (!ctx) {
    throw new Error('useTimelineContext must be used within a TimelineProvider');
  }
  return ctx;
}

export const TimelineProvider = TimelineContext.Provider;
